import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Alert, Tab, Tabs } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import styles from "./login.module.css";
import { googleAuth, login, logout, signup } from "../../service/auth";
import { UserInterface } from "../../interface/userInterface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faEnvelope,
  faShieldAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { resendOtp, sendOtpSignup, verifyOtp } from "../../service/otp";

interface LoginModalProps {
  children?: (
    handleOpen: () => void,
    isAuthenticated: boolean,
    user: UserInterface | null,
    handleSignOut: () => void
  ) => React.ReactNode;
  isStandalone?: boolean;
  onClose?: () => void;
  show?: boolean;
}
interface OTPState {
  sent: boolean;
  code: string;
  verified: boolean;
  timer: number;
  attempts: number;
  loading: boolean;
  error: string | null;
  success: string | null;
}
const LoginModal: React.FC<LoginModalProps> = ({
  children,
  isStandalone = false,
  onClose,
  show = false,
}) => {
  const [internalShow, setInternalShow] = useState(isStandalone);
  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    emailAddress: "",
    password: "",
    phone: "",
  });

  //OTP
  const [otpState, setOtpState] = useState<OTPState>({
    sent: false,
    code: "",
    verified: false,
    timer: 0,
    attempts: 0,
    loading: false,
    error: null,
    success: null,
  });

  const handleSendOtp = async () => {
    if (!formData.emailAddress) {
      setOtpState((prev: any) => ({
        ...prev,
        error: "Please enter a valid email address first.",
      }));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      setOtpState((prev: any) => ({
        ...prev,
        error: "Please enter a valid email address.",
      }));
      return;
    }

    try {
      setOtpState((prev: any) => ({
        ...prev,
        loading: true,
        error: null,
        success: null,
      }));

      const response = await sendOtpSignup(
        formData.emailAddress,
        formData.name
      );

      setOtpState((prev: any) => ({
        ...prev,
        sent: true,
        timer: 300, // 5 minutes
        loading: false,
        success: response.message || "OTP sent to your email!",
        attempts: 0,
      }));
    } catch (err: any) {
      setOtpState((prev: any) => ({
        ...prev,
        loading: false,
        error:
          err.response?.data?.error || "Failed to send OTP. Please try again.",
      }));
    }
  };

  const handleVerifyOtp = async () => {
    if (otpState.code.length !== 6 || !/^\d+$/.test(otpState.code)) {
      setOtpState((prev: any) => ({
        ...prev,
        error: "Please enter a valid 6-digit OTP.",
      }));
      return;
    }

    try {
      setOtpState((prev: any) => ({ ...prev, loading: true, error: null }));

      const response = await verifyOtp(formData.emailAddress, otpState.code);

      setOtpState((prev: any) => ({
        ...prev,
        verified: true,
        loading: false,
        success: "OTP verified successfully!",
        error: null,
      }));
    } catch (err: any) {
      const errorData = err.response?.data;
      setOtpState((prev: any) => ({
        ...prev,
        loading: false,
        error: errorData?.error || "OTP verification failed.",
        attempts:
          errorData?.remainingAttempts !== undefined
            ? 5 - errorData.remainingAttempts
            : prev.attempts + 1,
      }));
    }
  };

  const handleResendOtp = async () => {
    try {
      setOtpState((prev: any) => ({ ...prev, loading: true, error: null }));

      const response = await resendOtp(formData.emailAddress, formData.name);

      setOtpState((prev: any) => ({
        ...prev,
        timer: 300,
        loading: false,
        success: "New OTP sent to your email!",
        code: "",
        attempts: 0,
      }));
    } catch (err: any) {
      setOtpState((prev: any) => ({
        ...prev,
        loading: false,
        error:
          err.response?.data?.error ||
          "Failed to resend OTP. Please try again.",
      }));
    }
  };

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (otpState.sent && otpState.timer > 0) {
      interval = setInterval(() => {
        setOtpState((prev: any) => ({ ...prev, timer: prev.timer - 1 }));
      }, 1000);
    } else if (otpState.timer === 0 && otpState.sent) {
      setOtpState((prev: any) => ({
        ...prev,
        error: "OTP has expired. Please request a new one.",
      }));
    }

    return () => clearInterval(interval);
  }, [otpState.sent, otpState.timer]);

  // Clear OTP state when switching tabs or closing
  useEffect(() => {
    if (!internalShow || activeTab === "login") {
      setOtpState({
        sent: false,
        code: "",
        verified: false,
        timer: 0,
        attempts: 0,
        loading: false,
        error: null,
        success: null,
      });
    }
  }, [internalShow, activeTab]);

  //User
  const { isAuthenticated, user, login: contextLogin } = useAuth();
  const navigate = useNavigate();

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Sync internal show state with prop
  useEffect(() => {
    if (!isStandalone) {
      setInternalShow(show);
    }
  }, [show, isStandalone]);

  const handleOpen = () => {
    setInternalShow(true);
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    setInternalShow(false);
    if (onClose) {
      onClose();
    }
    if (isStandalone) {
      navigate(-1);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { token, user } = await login(
        formData.emailAddress,
        formData.password
      );
      await contextLogin(token, user);
      setSuccess("Login successful!");
      const currentPath = window.location.pathname;
      const redirectPath =
        user.role === "SUPER_ADMIN" ? "/admin-panel" : currentPath;
      setTimeout(() => {
        handleClose();
        setTimeout(() => {
          navigate(redirectPath);
        }, 1500);
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const currentPath = window.location.pathname;
      const { token, user } = await signup(formData);
      await contextLogin(token, user);
      setSuccess("Account created successfully!");
      setTimeout(() => {
        handleClose();
        navigate(currentPath);
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (isStandalone) {
    return (
      <div className={styles.standaloneContainer}>
        <div className={styles.standaloneModal}>
          <div className={styles.modalHeader}>
            <div className={styles.logoContainer}>
              <img
                src="/images/scadellogosvg.png"
                alt="Scadel Logo"
                className={styles.logoImg}
              />
            </div>
            <p className={styles.modalSubtitle}>
              {activeTab === "login"
                ? "Sign in to access your account"
                : "Join us to explore our furniture collections"}
            </p>
          </div>
          <div className={styles.modalBody}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k as "login" | "signup")}
              className={styles.authTabs}
            >
              <Tab
                eventKey="login"
                title="Sign In"
                className={styles.tabContent}
              >
                <div className={styles.tabInner}>
                  {error && (
                    <Alert variant="danger" className={styles.alert}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert variant="success" className={styles.alert}>
                      {success}
                    </Alert>
                  )}

                  <form onSubmit={handleLogin} className={styles.authForm}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Email Address</label>
                      <input
                        type="email"
                        name="emailAddress"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        className={styles.formInput}
                        required
                        placeholder="Enter your email"
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Password</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={styles.formInput}
                        required
                        placeholder="Enter your password"
                      />
                      <Link
                        to="/forgot-password"
                        className={styles.forgotPassword}
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Button
                      type="submit"
                      className={styles.authButton}
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className={styles.spinner}
                        />
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>

                  <div className={styles.socialAuth}>
                    <div className={styles.socialDivider}>
                      <span>Or continue with</span>
                    </div>
                    <div className={styles.socialButtons}>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (!process.env.REACT_APP_API_HOST) {
                            setError("Google authentication is not configured");
                            return;
                          }
                          localStorage.setItem(
                            "preAuthPath",
                            window.location.pathname
                          );
                          googleAuth();
                        }}
                        className={`${styles.socialButton} ${styles.googleButton}`}
                      >
                        <i className="fab fa-google"></i> Google
                      </Button>
                    </div>
                  </div>
                </div>
              </Tab>

              <Tab
                eventKey="signup"
                title="Sign Up"
                className={styles.tabContent}
              >
                <div className={styles.tabInner}>
                  {error && (
                    <Alert variant="danger" className={styles.alert}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert variant="success" className={styles.alert}>
                      {success}
                    </Alert>
                  )}

                  <form onSubmit={handleSignup} className={styles.authForm}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Email Address</label>
                      <input
                        type="email"
                        name="emailAddress"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        className={styles.formInput}
                        required
                        placeholder="Enter your email"
                      />
                    </div>
                    <div className={styles.formGroup}></div>

                    {formData.emailAddress && activeTab === "signup" && (
                      <div className={styles.otpSection}>
                        <div className={styles.otpHeader}>
                          <FontAwesomeIcon
                            icon={faShieldAlt}
                            className={styles.otpIcon}
                          />
                          <span>Email Verification</span>
                          {otpState.sent && (
                            <div className={styles.timerBadge}>
                              <FontAwesomeIcon icon={faClock} />
                              {Math.floor(otpState.timer / 60)}:
                              {(otpState.timer % 60)
                                .toString()
                                .padStart(2, "0")}
                            </div>
                          )}
                        </div>

                        {!otpState.sent ? (
                          <Button
                            variant="outline-primary"
                            onClick={handleSendOtp}
                            disabled={otpState.loading}
                            className={styles.sendOtpButton}
                          >
                            {otpState.loading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faEnvelope} />
                                Send Verification OTP
                              </>
                            )}
                          </Button>
                        ) : (
                          <>
                            <div className={styles.otpInputGroup}>
                              <label>Enter 6-digit OTP</label>
                              <div className={styles.otpContainer}>
                                <input
                                  type="text"
                                  maxLength={6}
                                  value={otpState.code}
                                  onChange={(e) =>
                                    setOtpState((prev) => ({
                                      ...prev,
                                      code: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                    }))
                                  }
                                  className={styles.otpInput}
                                  placeholder="••••••"
                                  disabled={
                                    otpState.verified || otpState.loading
                                  }
                                />
                                <Button
                                  variant={
                                    otpState.verified ? "success" : "primary"
                                  }
                                  onClick={handleVerifyOtp}
                                  disabled={
                                    otpState.verified ||
                                    otpState.loading ||
                                    otpState.code.length !== 6
                                  }
                                  className={styles.verifyButton}
                                >
                                  {otpState.loading ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : otpState.verified ? (
                                    "Verified ✓"
                                  ) : (
                                    "Verify"
                                  )}
                                </Button>
                              </div>
                            </div>

                            <div className={styles.otpActions}>
                              <Button
                                variant="link"
                                onClick={handleResendOtp}
                                disabled={
                                  otpState.timer > 0 ||
                                  otpState.loading ||
                                  otpState.verified
                                }
                                className={styles.resendButton}
                              >
                                {otpState.loading ? "Sending..." : "Resend OTP"}
                              </Button>

                              {otpState.attempts > 0 && (
                                <span className={styles.attemptsWarning}>
                                  {5 - otpState.attempts} attempt(s) remaining
                                </span>
                              )}
                            </div>
                          </>
                        )}

                        {/* OTP Status Messages */}
                        {otpState.error && (
                          <Alert variant="danger" className={styles.otpAlert}>
                            {otpState.error}
                          </Alert>
                        )}

                        {otpState.success && (
                          <Alert variant="success" className={styles.otpAlert}>
                            {otpState.success}
                          </Alert>
                        )}

                        {/* OTP Instructions */}
                        {otpState.sent && !otpState.verified && (
                          <div className={styles.otpInstructions}>
                            <p>• Check your email for the 6-digit code</p>
                            <p>• Code expires in 5 minutes</p>
                            <p>• Do not share this code with anyone</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={styles.formInput}
                        placeholder="Enter your phone (optional)"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Password</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={styles.formInput}
                        required
                        minLength={8}
                        placeholder="Create a password"
                      />
                    </div>
                    <Button
                      type="submit"
                      className={styles.authButton}
                      disabled={loading || !otpState.verified}
                    >
                      {loading ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className={styles.spinner}
                        />
                      ) : (
                        "Create Account"
                      )}
                    </Button>
                  </form>

                  <div className={styles.termsText}>
                    By signing up, you agree to our{" "}
                    <Link to="/terms">Terms</Link> and{" "}
                    <Link to="/privacy">Privacy Policy</Link>.
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.LoginModalParent}>
      {children &&
        children(handleOpen, isAuthenticated, user, () => {
          logout().finally(() => {
            window.location.reload();
          });
        })}

      <Modal
        show={internalShow}
        onHide={handleClose}
        centered
        className={`${styles.modal} ${isMobile ? styles.mobileModal : ""}`}
        backdropClassName={styles.modalBackdrop}
        dialogClassName={isMobile ? styles.mobileDialog : ""}
        contentClassName={isMobile ? styles.mobileContent : ""}
        animation={!isMobile}
        scrollable={isMobile}
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerGradient}></div>
          <div className={styles.headerContent}>
            <div className={styles.logoContainer}>
              <img
                src="/images/scadellogosvg.png"
                alt="Scadel Logo"
                className={styles.logoImg}
              />
            </div>

            <p className={styles.modalSubtitle}>
              {activeTab === "login"
                ? "Sign in to access your account"
                : "Create your Account"}
            </p>
          </div>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <Modal.Body className={styles.modalBody}>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as "login" | "signup")}
            className={styles.authTabs}
          >
            <Tab eventKey="login" title="Sign In" className={styles.tabContent}>
              <div className={styles.tabInner}>
                {error && (
                  <Alert variant="danger" className={styles.alert}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" className={styles.alert}>
                    {success}
                  </Alert>
                )}

                <form onSubmit={handleLogin} className={styles.authForm}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email Address</label>
                    <input
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      className={styles.formInput}
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={styles.formInput}
                      required
                      placeholder="Enter your password"
                    />
                    <Link
                      to="/forgot-password"
                      className={styles.forgotPassword}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Button
                    type="submit"
                    className={styles.authButton}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className={styles.spinner}
                      />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>

                <div className={styles.socialAuth}>
                  <div className={styles.socialDivider}>
                    <span>Or continue with</span>
                  </div>
                  <div className={styles.socialButtons}>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!process.env.REACT_APP_API_HOST) {
                          setError("Google authentication is not configured");
                          return;
                        }
                        localStorage.setItem(
                          "preAuthPath",
                          window.location.pathname
                        );
                        googleAuth();
                      }}
                      className={`${styles.socialButton} ${styles.googleButton}`}
                    >
                      <i className="fab fa-google"></i> Google
                    </Button>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab
              eventKey="signup"
              title="Sign Up"
              className={styles.tabContent}
            >
              <div className={styles.tabInner}>
                {error && (
                  <Alert variant="danger" className={styles.alert}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" className={styles.alert}>
                    {success}
                  </Alert>
                )}

                <form onSubmit={handleSignup} className={styles.authForm}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={styles.formInput}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email Address</label>
                    <input
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      className={styles.formInput}
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                  <div className={styles.formGroup}></div>

                  {formData.emailAddress && activeTab === "signup" && (
                    <div className={styles.otpSection}>
                      <div className={styles.otpHeader}>
                        <FontAwesomeIcon
                          icon={faShieldAlt}
                          className={styles.otpIcon}
                        />
                        <span>Email Verification</span>
                        {otpState.sent && (
                          <div className={styles.timerBadge}>
                            <FontAwesomeIcon icon={faClock} />
                            {Math.floor(otpState.timer / 60)}:
                            {(otpState.timer % 60).toString().padStart(2, "0")}
                          </div>
                        )}
                      </div>

                      {!otpState.sent ? (
                        <Button
                          variant="outline-primary"
                          onClick={handleSendOtp}
                          disabled={otpState.loading}
                          className={styles.sendOtpButton}
                        >
                          {otpState.loading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faEnvelope} />
                              Send Verification OTP
                            </>
                          )}
                        </Button>
                      ) : (
                        <>
                          <div className={styles.otpInputGroup}>
                            <label>Enter 6-digit OTP</label>
                            <div className={styles.otpContainer}>
                              <input
                                type="text"
                                maxLength={6}
                                value={otpState.code}
                                onChange={(e) =>
                                  setOtpState((prev) => ({
                                    ...prev,
                                    code: e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 6),
                                  }))
                                }
                                className={styles.otpInput}
                                placeholder="••••••"
                                disabled={otpState.verified || otpState.loading}
                              />
                              <Button
                                variant={
                                  otpState.verified ? "success" : "primary"
                                }
                                onClick={handleVerifyOtp}
                                disabled={
                                  otpState.verified ||
                                  otpState.loading ||
                                  otpState.code.length !== 6
                                }
                                className={styles.verifyButton}
                              >
                                {otpState.loading ? (
                                  <Spinner animation="border" size="sm" />
                                ) : otpState.verified ? (
                                  "Verified ✓"
                                ) : (
                                  "Verify"
                                )}
                              </Button>
                            </div>
                          </div>

                          <div className={styles.otpActions}>
                            <Button
                              variant="link"
                              onClick={handleResendOtp}
                              disabled={
                                otpState.timer > 0 ||
                                otpState.loading ||
                                otpState.verified
                              }
                              className={styles.resendButton}
                            >
                              {otpState.loading ? "Sending..." : "Resend OTP"}
                            </Button>

                            {otpState.attempts > 0 && (
                              <span className={styles.attemptsWarning}>
                                {5 - otpState.attempts} attempt(s) remaining
                              </span>
                            )}
                          </div>
                        </>
                      )}

                      {/* OTP Status Messages */}
                      {otpState.error && (
                        <Alert variant="danger" className={styles.otpAlert}>
                          {otpState.error}
                        </Alert>
                      )}

                      {otpState.success && (
                        <Alert variant="success" className={styles.otpAlert}>
                          {otpState.success}
                        </Alert>
                      )}

                      {/* OTP Instructions */}
                      {otpState.sent && !otpState.verified && (
                        <div className={styles.otpInstructions}>
                          <p>• Check your email for the 6-digit code</p>
                          <p>• Code expires in 5 minutes</p>
                          <p>• Do not share this code with anyone</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={styles.formInput}
                      placeholder="Enter your phone (optional)"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={styles.formInput}
                      required
                      minLength={8}
                      placeholder="Create a password"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={styles.authButton}
                    disabled={loading || !otpState.verified}
                  >
                    {loading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className={styles.spinner}
                      />
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>

                <div className={styles.termsText}>
                  By signing up, you agree to our <Link to="/terms">Terms</Link>{" "}
                  and <Link to="/privacy">Privacy Policy</Link>.
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LoginModal;
